import React, { useState, useContext, useCallback } from "react";
import { matchPath } from "react-router-dom";

import { IDialogProps } from "@plex/react-components";

import { useLocalizer } from "ui/hooks";

import { IAppRouteProps } from "../IAppRouteProps";

export interface IDialogRouterContext {
  routes: IAppRouteProps[];
}

export const DialogRouterContext = React.createContext<IDialogRouterContext>({ routes: [] });

export interface IDialogRouterProps {
  routes: IAppRouteProps[];
}

export const DialogRouter: React.FC<IDialogRouterProps> = ({ routes, children }) => {
  return (
    <DialogRouterContext.Provider
      value={{
        routes: routes
      }}
    >
      <DialogNavigationProvider>{children}</DialogNavigationProvider>
    </DialogRouterContext.Provider>
  );
};

export interface IDialogRouteProps {
  to?: {
    location: string;
    data?: any;
  };
  content?: React.ReactNode | null;
}

export interface IDialogNavigationContext {
  showDialog: (route: IDialogRouteProps) => void;
  hideDialog: (onHide?: () => boolean, onHidden?: () => void) => void;
  parentContext: IDialogNavigationContext | null;
}

export const DialogNavigationContext = React.createContext<IDialogNavigationContext>({
  parentContext: null,
  showDialog: () => {},
  hideDialog: () => {}
});

export interface IDialogContext {
  isOpened: boolean;
  title?: string;
}

export const DialogContext = React.createContext<IDialogContext>({ isOpened: false });

const DialogRouteContent: React.FC<IDialogRouteProps> = ({ to, content }) => {
  const routerContext = useContext(DialogRouterContext);

  let routeParams = to ? { ...to.data } : {};
  let routeProps = {};

  const localize = useLocalizer();

  if (to && to.location) {
    const route = routerContext.routes
      .map(appRoute => {
        let result: { props: IAppRouteProps; match: any | null } | null = null;

        const routeMatch = matchPath(to.location, appRoute);

        if (routeMatch) {
          result = { props: appRoute, match: routeMatch };
        }

        return result;
      })
      .filter(e => e)[0];

    if (route) {
      routeParams = { ...routeParams, ...route.match.params };
      routeProps = { match: route.match };

      const DialogContent = route.props.component;

      return (
        <DialogContext.Provider value={{ isOpened: true, title: localize(route.props.title) }}>
          <DialogContent {...routeProps} {...routeParams} />
        </DialogContext.Provider>
      );
    } else {
      return null;
    }
  } else {
    return <DialogContext.Provider value={{ isOpened: true }}>{content}</DialogContext.Provider>;
  }
};

const DialogNavigationProvider: React.FC<{ parentContext?: IDialogNavigationContext }> = ({ children, parentContext = null }) => {
  const [dialogRoute, setDialogRoute] = useState<IDialogRouteProps | undefined>({});

  return (
    <DialogNavigationContext.Provider
      value={{
        showDialog: (route: IDialogRouteProps) => {
          setDialogRoute(route);
        },
        hideDialog: (onHide?: () => boolean, onHidden?: () => void) => {
          if (onHide == null || onHide()) {
            setDialogRoute(undefined);

            onHidden && onHidden();
          }
        },
        parentContext: parentContext
      }}
    >
      <DialogRouteContent {...dialogRoute} />
      {children}
    </DialogNavigationContext.Provider>
  );
};

export interface IDialogContainerProps {
  onHide?: () => boolean;
  onHidden?: () => void;
  render: (dialogProps: IDialogProps) => React.ReactNode;
}

export const DialogContainer: React.FC<IDialogContainerProps> = ({ render, onHide, onHidden }) => {
  const navigationContext = useContext(DialogNavigationContext);
  const dialogContext = useContext(DialogContext);

  const hideCallback = useCallback(() => {
    navigationContext.hideDialog(onHide, onHidden);
  }, [navigationContext, onHide, onHidden]);

  const dialogProps: IDialogProps = { show: dialogContext.isOpened, onHide: hideCallback, backdrop: "static", title: dialogContext.title };

  return dialogContext.isOpened ? <DialogNavigationProvider parentContext={navigationContext}>{render(dialogProps)}</DialogNavigationProvider> : null;
};
